import { Component, OnInit } from '@angular/core';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { plazoDe, nombreDelPlazo, conUnidad, errorDelPlazo, comisionDeFila } from '../plazo-unidad';
import { USO_POR_OMISION } from '../uso-tecnologico';

/**
 * TARIFARIO DE LIQUIDACIÓN — la tabla única de plazos de la que salen el nombre del plazo y la
 * comisión al cargar las condiciones de un comercio (Mario, 07-oct-2026).
 *
 * Versionada: una versión nueva rige desde su fecha y cierra la vigente; las versiones de condiciones
 * guardan qué tabla usaron. Mientras no haya ninguna en la base, rige la provisional del 05-oct (V1).
 * Reglas (las mismas en el servidor): plazo entero desde 1 en horas o días, nombre, comisión entre 0 y 100 %.
 * TRF-10 (Mario, 26-oct): sin tope (antes 5 %), horas también en cesión de deuda, cada fila con % descuento + % uso de
 * plataforma, que es un cargo aparte y NO se suma a la comisión (TRF-12), y el mismo plazo con nombres distintos.
 */
@Component({
  selector: 'app-tarifario',
  templateUrl: './tarifario.component.html'
})
export class TarifarioComponent implements OnInit {
  readonly TOPE = 100;

  cargando = true;
  error = '';
  versiones: any[] = [];

  // ── versión nueva ──
  creando = false;
  guardando = false;
  errorGuardar = '';
  aviso = '';
  nueva: any = this.nuevaVacia();

  /** Una tabla por tipo de contrato (Mario, 11-oct). */
  tipo: 'COMPRA_DEUDA' | 'COBRANZA' = 'COMPRA_DEUDA';

  constructor(private merchants: MerchantService, public _auth: AuthService) { }

  /** PAR-07 (Mario, 12-oct): versiona el tarifario quien puede aprobar condiciones (la gerente de finanzas o el director). */
  get puedeEditar(): boolean { return this._auth.hasPermission('config.comercios.terms.approve'); }

  verTipo = (t: 'COMPRA_DEUDA' | 'COBRANZA'): void => { if (this.tipo === t) return; this.tipo = t; this.creando = false; this.aviso = ''; this.cargar(); };

  ngOnInit(): void { this.cargar(); }

  get vigente(): any {
    const ahora = Date.now();
    return this.versiones.find(v => new Date(v.version.date_init ?? 0).getTime() <= ahora
      && (!v.version.date_end || new Date(v.version.date_end).getTime() > ahora)) || this.versiones[0] || null;
  }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try { this.versiones = await this.merchants.getRateTables(this.tipo); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo leer el tarifario.'; }
    finally { this.cargando = false; }
  };

  private manana(): string {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private nuevaVacia(): any { return { desde: this.manana(), provisional: false, nota: '', rows: [] as any[] }; }

  abrirNueva = (): void => {
    const base = this.vigente;
    this.nueva = this.nuevaVacia();
    // PLZ-01: cada fila conserva su unidad (sin unidad = días); en la pantalla se edita el valor en su unidad.
    // TRF-10: una fila sin desglose arranca con la comisión entera como descuento y 0 de uso de plataforma.
    this.nueva.rows = (base?.rows || []).map((r: any) => ({ unit: plazoDe(r).unidad, valor: plazoDe(r).valor, label: r.label,
      discount_value: r.discount_value ?? r.commission_value, platform_value: r.platform_value ?? 0 }));
    if (!this.nueva.rows.length) this.agregarFila();
    this.creando = true; this.errorGuardar = ''; this.aviso = '';
  };

  /** PLT-01 (Mario, 30-oct): el uso de plataforma ya no se edita aquí; una fila nueva toma el de la tabla (2 % si no hay). */
  plataformaDeLaTabla = (): number => {
    const v = (this.nueva?.rows || []).map((r: any) => r.platform_value).find((x: any) => x !== null && x !== undefined && x !== '');
    return v === undefined ? USO_POR_OMISION : Number(v);
  };

  agregarFila = (): void => { this.nueva.rows.push({ unit: 'DIAS', valor: null, label: '', discount_value: null, platform_value: this.plataformaDeLaTabla() }); };

  /** La comisión de la fila (solo el descuento; la plataforma va aparte), o null si falta una parte. */
  total = (r: any): number | null => comisionDeFila(r);
  quitarFila = (i: number): void => { this.nueva.rows.splice(i, 1); };

  /** Lo mismo que valida el servidor, dicho antes de enviar. */
  get errores(): string[] {
    const e: string[] = [];
    if (!this.nueva.desde) e.push('Falta la fecha desde la que rige');
    if (!this.nueva.rows.length) e.push('La tabla necesita al menos un plazo');
    const vistos = new Set<string>();
    this.nueva.rows.forEach((r: any, i: number) => {
      const unidad = r.unit || 'DIAS';
      const mal = errorDelPlazo(unidad, r.valor, this.tipo);
      const clave = `${unidad}:${Number(r.valor)}:${String(r.label || '').trim().toLowerCase()}`;
      // PLZ-02/TRF-10: no se repite el mismo plazo con la misma unidad y el mismo nombre (Aliado preferencial y Aliado general conviven).
      if (mal) e.push(`Fila ${i + 1}: ${mal.charAt(0).toLowerCase()}${mal.slice(1)}`);
      else if (vistos.has(clave)) e.push(`Fila ${i + 1}: el plazo de ${nombreDelPlazo({ unidad, valor: Number(r.valor) })} con ese nombre está repetido`);
      vistos.add(clave);
      if (!String(r.label || '').trim()) e.push(`Fila ${i + 1}: falta el nombre del plazo`);
      const fuera = (x: any) => x === null || x === '' || x === undefined || !(Number(x) >= 0) || Number(x) > this.TOPE;
      if (fuera(r.discount_value)) e.push(`Fila ${i + 1}: el descuento debe estar entre 0 y ${this.TOPE} %`);
      else if (fuera(r.platform_value)) e.push(`Fila ${i + 1}: el uso de plataforma debe estar entre 0 y ${this.TOPE} %`);
    });
    return e;
  }

  guardar = async (): Promise<void> => {
    if (this.errores.length) return;
    this.guardando = true; this.errorGuardar = '';
    try {
      // Fecha local a medianoche de Venezuela (UTC-4).
      const desde = new Date(`${this.nueva.desde}T00:00:00-04:00`).toISOString();
      const rows = this.nueva.rows.map((x: any) => ({ ...conUnidad(x.unit || 'DIAS', x.valor), label: x.label,
        discount_value: x.discount_value, platform_value: x.platform_value, commission_value: this.total(x) }));
      const r = await this.merchants.createRateTable({ ...this.nueva, rows, desde, business_model: this.tipo });
      this.aviso = `Se creó ${r.tarifario?.version?.code}. Rige desde el ${this.nueva.desde}.`;
      this.creando = false;
      await this.cargar();
    } catch (e: any) {
      this.errorGuardar = e?.error?.msg || 'No se pudo crear la versión.';
    } finally { this.guardando = false; }
  };

  /** PLZ-03: el plazo con su unidad («24 horas», «1 día», «15 días»). Antes: 1 día se leía «24 horas». */
  textoPlazo = (r: any): string => nombreDelPlazo(plazoDe(r));
}
